import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog } from "@akuna/ui";
import type { StayPlanContract } from "@/hooks/useStayPlanContracts";
import { FixedWeekdaysField } from "@/pages/daycare-hotel/plans/FixedWeekdaysField";
import { useChangeFixedWeekdays } from "@/pages/daycare-hotel/plans/useDaycarePlanContractDays";

export interface EditPlanDaysDialogProps {
  contract: StayPlanContract;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Troca os dias fixos da matrícula. A RPC reagenda as diárias a partir de hoje,
 * por isso o salvar só libera com a quantidade contratada e alguma mudança.
 */
export function EditPlanDaysDialog({ contract, open, onOpenChange }: EditPlanDaysDialogProps) {
  const { t } = useTranslation("patients");
  const changeDays = useChangeFixedWeekdays();
  const [weekdays, setWeekdays] = useState<number[]>(contract.fixedWeekdays);
  const required = contract.usageLimit ?? 0;

  useEffect(() => {
    if (open) setWeekdays(contract.fixedWeekdays);
  }, [open, contract.fixedWeekdays]);

  const unchanged =
    weekdays.length === contract.fixedWeekdays.length &&
    weekdays.every((day) => contract.fixedWeekdays.includes(day));
  const canSave = weekdays.length === required && !unchanged && !changeDays.isPending;

  const submit = async () => {
    await changeDays.mutateAsync({ contractId: contract.id, weekdays });
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      mode="adaptive"
      className="sm:max-w-[480px]"
      title={t("planCard.editDays.title")}
      header={contract.planName}
      closeLabel={t("planCard.editDays.cancel")}
      footer={
        <div className="flex w-full items-center justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("planCard.editDays.cancel")}
          </Button>
          <Button type="button" disabled={!canSave} onClick={() => void submit()}>
            {t("planCard.editDays.save")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t("planCard.editDays.description")}</p>
        <FixedWeekdaysField
          value={weekdays}
          required={required}
          onChange={setWeekdays}
          disabled={changeDays.isPending}
        />
      </div>
    </Dialog>
  );
}
